import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import DOMPurify from 'dompurify';
import { useSeoContentHide, usePhotoModal } from 'hooks';
import { trackViewTeacher } from 'utils/analytics';
import { getBannerStyle, getBannerHeight } from 'utils';
import useStudentLogin from 'hooks/useStudentLogin';
import { getBlockStyle, generateBlockStyle, getTextStyle, generateTextStyleClasses, generateTextStyleInlineStyles } from 'utils/blockStyleUtils';
import UserAvatar from './shared/UserAvatar';
import PhotoModal from './PhotoModal';
import BookingFlowModal from './BookingFlowModal';
import EditBar from './EditBar';
import { ReviewsBlock, TeacherNameBlock, ReferralStatsBlock, BioBlock, LinksBlock, PhotosBlock } from './shared/blocks';

const TeacherPublicPage = () => {
  const [teacher, setTeacher] = useState(null);
  const [locale, setLocale] = useState('zh-TW');
  const [translations, setTranslations] = useState({});
  const [visitTracking, setVisitTracking] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [pageDesign, setPageDesign] = useState(null);
  const [customBlocks, setCustomBlocks] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  // Photo modal state management
  const {
    isOpen: isPhotoModalOpen,
    photos: modalPhotos,
    currentIndex: photoCurrentIndex,
    openModal: openPhotoModal,
    closeModal: closePhotoModal,
    modalRef: photoModalRef,
    handleOverlayClick: handlePhotoOverlayClick
  } = usePhotoModal();

  // BookingFlow modal state management
  const [isBookingFlowModalOpen, setIsBookingFlowModalOpen] = useState(false);

  // User authentication state (for booking access control)
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const openBookingFlowModal = () => {
    setIsBookingFlowModalOpen(true);
  };

  const closeBookingFlowModal = () => {
    setIsBookingFlowModalOpen(false);
  };

  const handleBookingComplete = (bookingData) => {
    // Phase 1: Show booking data in alert
    alert(`Booking details:\nCourse: ${bookingData.eventTypeTitle}\nTime: ${bookingData.date} ${bookingData.slot}\nDuration: ${bookingData.duration} min\nPrice: ${bookingData.currency} ${bookingData.price}`);
    // Phase 2 TODO: Create booking via API, handle payment, show confirmation
  };

  // Check user authentication status
  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/auth/status', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success && data.authenticated && data.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
      setUser(null);
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // Google OAuth, or the email login in an in-app browser
  const { startStudentLogin: redirectToStudentLogin, loginModal } = useStudentLogin(translations);

  // Hide SEO content when React loads
  useSeoContentHide({ selector: '.teacher-seo-content' });

  // Get translation from backend data
  const getTranslation = (key) => {
    return translations[key];
  };

  // Get language label translation
  const getLanguageLabel = (languageKey) => {
    return translations.shared?.teaching_languages?.[languageKey];
  };

  // Get specialty label translation
  const getSpecialtyLabel = (specialtyKey) => {
    return translations.shared?.subject_tags?.[specialtyKey];
  };

  useEffect(() => {
    // Get data from data attributes
    const container = document.getElementById('teacher-public-page');
    if (container) {
      try {
        const teacherData = JSON.parse(container.dataset.teacher);
        const pageDesignData = JSON.parse(container.dataset.pageDesign || 'null');

        setTeacher(teacherData);
        setLocale(container.dataset.locale || 'zh-TW');
        setTranslations(JSON.parse(container.dataset.translations || '{}'));
        setVisitTracking(JSON.parse(container.dataset.visitTracking || '{}'));
        setReviews(JSON.parse(container.dataset.reviews || '{}'));
        setPageDesign(pageDesignData);
        setCustomBlocks(JSON.parse(container.dataset.customBlocks || '[]'));
        setTimezoneOptions(JSON.parse(container.dataset.timezoneOptions || '[]'));
      } catch (error) {
        console.error('Error parsing teacher data:', error);
      }
    }
  }, []);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Track view_item event when teacher profile is viewed
  useEffect(() => {
    if (teacher?.id) {
      trackViewTeacher(teacher.id, teacher.display_name || teacher.name);
    }
  }, [teacher]);

  // Debug: Check actual DOM dimensions after render
  useEffect(() => {
    if (teacher && pageDesign) {
      setTimeout(() => {
        const container = document.querySelector('[style*="max-width: 42rem"]');
        const banner = container?.querySelector('[style*="background"]');
        const avatar = container?.querySelector('.text-center');

        console.log('📐 Actual DOM Dimensions:');
        console.log('  Main container width:', container?.offsetWidth, 'px');
        console.log('  Banner element:', banner);
        console.log('  Banner computed width:', banner ? window.getComputedStyle(banner).width : 'N/A');
        console.log('  Avatar element:', avatar);
        console.log('  Avatar computed position:', avatar ? window.getComputedStyle(avatar).position : 'N/A');
        console.log('  Avatar computed top:', avatar ? window.getComputedStyle(avatar).top : 'N/A');
        console.log('  Avatar computed left:', avatar ? window.getComputedStyle(avatar).left : 'N/A');
      }, 100);
    }
  }, [teacher, pageDesign]);

  // Wait for both teacher and pageDesign to load before rendering
  if (!teacher || pageDesign === null) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">{getTranslation('loading')}</div>;
  }

  const handlePhotoClick = (clickedPhotoIndex) => {
    if (teacher.selected_photos && teacher.selected_photos.length > 0) {
      openPhotoModal(teacher.selected_photos, clickedPhotoIndex);
    }
  };

  // Get background style based on page design
  const getBackgroundStyle = () => {
    if (!pageDesign?.background) {
      return { backgroundColor: teacher.background_color || '#f8fafc' };
    }

    const bg = pageDesign.background;
    if (bg.type === 'color') {
      return { backgroundColor: bg.color || '#ffffff' };
    } else if (bg.type === 'gradient') {
      return {
        background: `linear-gradient(135deg, ${bg.gradient_start || '#ffffff'}, ${bg.gradient_end || '#f3f4f6'})`
      };
    } else if (bg.type === 'custom' && bg.image_url) {
      return {
        backgroundImage: `url(${bg.image_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      };
    }
    return { backgroundColor: '#f8fafc' };
  };

  // Use shared banner utilities from utils/bannerUtils.js
  const bannerHeightRaw = getBannerHeight(pageDesign);
  // Convert to number: '200px' -> 200, or use raw number if already a number
  const bannerHeightValue = typeof bannerHeightRaw === 'string'
    ? parseInt(bannerHeightRaw, 10)
    : bannerHeightRaw;
  const bannerStyle = getBannerStyle(pageDesign, { returnNullIfNoBanner: true });

  // Debug: Avatar positioning calculation
  console.log('🎯 Avatar Positioning Debug:');
  console.log('  pageDesign.avatar_offset_x:', pageDesign?.avatar_offset_x);
  console.log('  pageDesign.avatar_offset_y:', pageDesign?.avatar_offset_y);
  console.log('  bannerHeightRaw:', bannerHeightRaw);
  console.log('  bannerHeightValue:', bannerHeightValue, typeof bannerHeightValue);
  console.log('  Calculated top:', bannerHeightValue + (Number(pageDesign?.avatar_offset_y) || -64));
  console.log('  bannerStyle:', bannerStyle);

  // Check if a block should be visible
  const isBlockVisible = (blockType) => {
    if (!pageDesign?.visible_blocks) return true; // Default to visible if no design
    return pageDesign.visible_blocks[blockType] !== false;
  };

  // Get block rendering order
  const getBlockOrder = () => {
    if (!pageDesign?.layout_containers || pageDesign.layout_containers.length === 0) {
      // Default order if no page design
      return [
        { type: 'bio' },
        { type: 'reviews' },
        { type: 'availability' },
        { type: 'photos' },
        { type: 'links' }
      ];
    }
    // Phase 1: Use first container (single column)
    return pageDesign.layout_containers[0].blocks || [];
  };

  // Render block functions
  const renderTeacherNameBlock = () => {
    if (!teacher.display_name || !isBlockVisible('teacher_name')) return null;

    const nameStyle = generateBlockStyle(getBlockStyle(pageDesign, 'teacher_name'));

    return (
      <div key="teacher_name" className="p-8 my-8" style={nameStyle}>
        <TeacherNameBlock
          displayName={teacher.display_name}
          pageDesign={pageDesign}
          translations={translations}
          mode="public"
        />
      </div>
    );
  };

  const renderReferralStatsBlock = () => {
    // Hide block if count is 0 or if visibility is turned off
    if (!teacher.referred_bookings_count || teacher.referred_bookings_count === 0 || !isBlockVisible('referral_stats')) return null;

    const referralStatsStyle = generateBlockStyle(getBlockStyle(pageDesign, 'referral_stats'));

    return (
      <div key="referral_stats" className="p-8 my-8" style={referralStatsStyle}>
        <ReferralStatsBlock
          count={teacher.referred_bookings_count}
          translations={translations}
          mode="public"
          pageDesign={pageDesign}
        />
      </div>
    );
  };

  const renderBioBlock = () => {
    if (!teacher.bio || !isBlockVisible('bio')) return null;
    const bioStyle = generateBlockStyle(getBlockStyle(pageDesign, 'bio'));
    return (
      <div key="bio" className="p-8 my-8" style={bioStyle}>
        <BioBlock
          bioText={teacher.bio}
          pageDesign={pageDesign}
          translations={translations}
          mode="public"
        />
      </div>
    );
  };

  const renderReviewsBlock = () => {
    if (!reviews || reviews.reviews_count === 0 || !isBlockVisible('reviews')) return null;

    const reviewsStyle = generateBlockStyle(getBlockStyle(pageDesign, 'reviews'));

    return (
      <div key="reviews" className="p-8 my-8" style={reviewsStyle}>
        <ReviewsBlock
          reviewsData={reviews}
          translations={translations}
          mode="public"
          moreReviewsUrl={`/${teacher.slug}/reviews`}
          locale={locale}
          pageDesign={pageDesign}
        />
      </div>
    );
  };

  const renderAvailabilityBlock = () => {
    // Booking feature is paused (Teaching Teams pivot) — hide entire block
    return null;

    if (!isBlockVisible('availability')) return null;

    const availabilityStyle = generateBlockStyle(getBlockStyle(pageDesign, 'availability'));
    const t = translations.bookings?.booking_section || {};

    // If booking is not available
    if (!teacher.booking_available) {
      return (
        <div key="availability" className="p-8 my-8" style={availabilityStyle}>
          <div className="rounded-2xl p-6 text-center opacity-80">
            {getTranslation('booking_paused')}
          </div>
        </div>
      );
    }

    // Authentication check in progress
    if (isLoadingAuth) {
      return (
        <div key="availability" className="p-8 my-8" style={availabilityStyle}>
          <div className="text-center text-gray-500">
            {translations.common?.loading || 'Loading...'}
          </div>
        </div>
      );
    }

    // Determine user role
    const isTeacher = user && user.roles && user.roles.includes('teacher');
    const isStudent = user && !isTeacher;

    return (
      <div key="availability" className="p-8 my-8" style={availabilityStyle}>
        {/* State 1: Logged-in teacher - Red error message */}
        {isTeacher && (
          <div className="border border-red-200 rounded-lg bg-red-50 p-6">
            <div className="bg-red-100 border border-red-300 rounded-md p-4">
              <p className="text-red-800 font-semibold mb-2">
                {t.teacher_cannot_book_message || 'You are currently logged in as a teacher and cannot book lessons with other teachers.'}
              </p>
              <p className="text-red-700">
                {t.use_student_account || 'To book a lesson, please log out and sign in with a student account.'}
              </p>
            </div>
          </div>
        )}

        {/* State 2: Logged-in student - Green welcome message + Book button */}
        {isStudent && (
          <div className="text-center">
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md inline-block">
              <span className="text-green-800">
                {(t.welcome_back || 'Welcome back, %{user_name}!').replace('%{user_name}', user.name)}
              </span>
            </div>
            <div>
              <button
                onClick={openBookingFlowModal}
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
              >
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t.book_now || 'Book Now'}
              </button>
            </div>
          </div>
        )}

        {/* State 3: Not logged in - Yellow prompt + Login button */}
        {!user && (
          <div className="text-center">
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md inline-block">
              <span className="text-yellow-800">
                {t.login_prompt || 'Please sign up or log in to enjoy a personalized booking experience'}
              </span>
            </div>
            <div>
              <button
                onClick={redirectToStudentLogin}
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
              >
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                {t.register_and_book || 'Log In to Book'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPhotosBlock = () => {
    if (!teacher.selected_photos || teacher.selected_photos.length === 0 || !isBlockVisible('photos')) return null;
    const photosStyle = generateBlockStyle(getBlockStyle(pageDesign, 'photos'));
    return (
      <div key="photos" className="p-8 my-8" style={photosStyle}>
        <PhotosBlock
          selectedPhotos={teacher.selected_photos}
          onPhotoClick={handlePhotoClick}
          translations={translations}
          mode="public"
        />
      </div>
    );
  };

  const renderLinksBlock = () => {
    if (!teacher.links || teacher.links.length === 0 || !isBlockVisible('links')) return null;
    const linksStyle = generateBlockStyle(getBlockStyle(pageDesign, 'links'));
    return (
      <div key="links" className="p-8 my-8" style={linksStyle}>
        <LinksBlock
          links={teacher.links}
          translations={translations}
          mode="public"
          pageDesign={pageDesign}
        />
      </div>
    );
  };


  const renderCustomBlock = (block) => {
    if (!isBlockVisible('custom')) return null;

    const customBlock = customBlocks.find(cb => cb.block_id === block.custom_block_id);
    if (!customBlock) return null;

    const content = customBlock.content_by_language?.[locale] ||
                   customBlock.content_by_language?.['zh-TW'] ||
                   '';

    const customBlockStyle = generateBlockStyle(getBlockStyle(pageDesign, block.custom_block_id));

    return (
      <div key={`custom-${block.custom_block_id}`} className="p-8 mb-8" style={customBlockStyle}>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">{customBlock.title}</h2>
        <div
          className="text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    );
  };

  // Render blocks dynamically based on layout order
  const renderBlocks = () => {
    const blocks = getBlockOrder();

    return blocks.map((block) => {
      switch (block.type) {
        case 'teacher_name':
          return renderTeacherNameBlock();
        case 'referral_stats':
          return renderReferralStatsBlock();
        case 'bio':
          return renderBioBlock();
        case 'reviews':
          return renderReviewsBlock();
        case 'availability':
          return renderAvailabilityBlock();
        case 'photos':
          return renderPhotosBlock();
        case 'links':
          return renderLinksBlock();
        case 'custom':
          return renderCustomBlock(block);
        default:
          return null;
      }
    });
  };

  return (
    <div
      className="min-h-screen"
      style={getBackgroundStyle()}
    >
      {/* Edit Bar - Show when teacher views their own page */}
      {/* Displays blue gradient for public pages, yellow warning for private pages */}
      {teacher.is_owner && (
        <EditBar
          teacherSlug={teacher.slug}
          isPublic={!teacher.is_viewing_own_private_page}
          translations={translations}
        />
      )}

      {/* Main container - matches EditableCanvas structure */}
      <div style={{
        position: 'relative',
        maxWidth: '42rem',
        margin: '0 auto',
        padding: '0 1rem'
      }}>
        {/* Banner */}
        {bannerStyle && (
          <div
            style={bannerStyle}
          />
        )}

        {/* Avatar - Positioned absolutely relative to main container */}
        <div
          className="text-center"
          style={(() => {
            const hasPositioning = pageDesign?.avatar_offset_x !== undefined && pageDesign?.avatar_offset_y !== undefined;
            const style = hasPositioning
              ? {
                  position: 'absolute',
                  left: `${pageDesign.avatar_offset_x}%`,
                  top: `${bannerHeightValue + (Number(pageDesign.avatar_offset_y) || -64)}px`,
                  transform: 'translateX(-50%)',
                  zIndex: 10
                }
              : {
                  position: 'relative',
                  marginTop: '-4rem'
                };

            console.log('🎯 Avatar Style Applied:');
            console.log('  hasPositioning:', hasPositioning);
            console.log('  Final style:', style);

            return style;
          })()}
        >
          <UserAvatar
            src={teacher.avatar_url}
            name={teacher.display_name}
            size="2xl"
            fallback="icon"
            className="mx-auto"
          />
        </div>

        {/* Content area - inside main container */}
        <div className="pb-12" style={(() => {
          // Calculate content padding-top based on avatar position
          // This controls the distance from avatar bottom to the first content (teacher name)
          const hasPositioning = pageDesign?.avatar_offset_x !== undefined && pageDesign?.avatar_offset_y !== undefined;
          if (hasPositioning) {
            const bannerBottom = bannerHeightValue; // Banner bottom = banner height
            const avatarTop = bannerHeightValue + (Number(pageDesign.avatar_offset_y) || -64);
            const avatarHeight = 128; // w-32 h-32
            const avatarBottom = avatarTop + avatarHeight;
            const fixedSpacing = 64; // Fixed spacing from avatar bottom to content text

            // New rule: paddingTop = (avatar bottom - banner bottom) + 64px
            const paddingTop = (avatarBottom - bannerBottom) + fixedSpacing;

            console.log('📐 Content Padding Calculation:');
            console.log('  bannerBottom:', bannerBottom);
            console.log('  avatarTop:', avatarTop);
            console.log('  avatarHeight:', avatarHeight);
            console.log('  avatarBottom:', avatarBottom);
            console.log('  fixedSpacing:', fixedSpacing);
            console.log('  Formula: (avatarBottom - bannerBottom) + fixedSpacing');
            console.log('  = (' + avatarBottom + ' - ' + bannerBottom + ') + ' + fixedSpacing);
            console.log('  Final paddingTop:', paddingTop);

            return { paddingTop: `${paddingTop}px` };
          }
          return { paddingTop: '3rem' }; // Fallback to py-12's original padding
        })()}>
          {/* Dynamic Blocks (including teacher_name) */}
          {renderBlocks()}

          {/* 頁尾 */}
          <div className="text-center mt-12 text-sm text-gray-500">
            <p>{getTranslation('copyright')}</p>
          </div>
        </div>
      </div>


      {/* Photo Modal */}
      <PhotoModal
        isOpen={isPhotoModalOpen}
        photos={modalPhotos}
        currentIndex={photoCurrentIndex}
        onClose={closePhotoModal}
        onOverlayClick={handlePhotoOverlayClick}
        modalRef={photoModalRef}
        locale={locale}
      />

      {/* BookingFlow Modal */}
      <BookingFlowModal
        isOpen={isBookingFlowModalOpen}
        onClose={closeBookingFlowModal}
        teacherId={teacher.id}
        teacherUuid={teacher.webhook_uuid}
        referrerTeacherSlug={visitTracking?.referrer_teacher_slug}
        referrerStudentUuid={visitTracking?.referrer_student_uuid}
        translations={translations}
        timezoneOptions={timezoneOptions}
        onComplete={handleBookingComplete}
      />

      {loginModal}
    </div>
  );
};

// Component mounting is handled by teacher-public.js entry point
// Removed duplicate mounting logic to prevent double rendering

export default TeacherPublicPage;
